import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack, Switch, Typography } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_BRANDS_TABLE,
  CHART_KEY_DASHBOARD_CATEGORIES_TABLE,
  CHART_KEY_DASHBOARD_CATEGORIES_TOP,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { useProducts } from "Hooks/useProducts";
import { useMemo, useState } from "react";
import {
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface ICategoriesBrandsTabProps {
  attributes: ApiChartAttribute[];
}

const insightFieldsBase = [
  ApiMetric.METRIC_PRODUCT_REVENUE,
  ApiMetric.METRIC_SALES_COUNT,
  ApiMetric.METRIC_PAGEVIEWS_COUNT,
  ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
];

function CategoriesBrandsTab({ attributes }: ICategoriesBrandsTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const insightFields = useMemo(
    () => withoutAddToCartMetrics(insightFieldsBase, isEcommerce),
    [isEcommerce],
  );
  const { productsWithBrands, productsWithCategories } = useProducts();
  const showCategory = productsWithCategories.length > 0;
  const showBrand = productsWithBrands.length > 1;
  const [mode, setMode] = useState<"category" | "brand">(
    showCategory ? "category" : "brand",
  );

  const dimension = useMemo(
    () =>
      mode === "category"
        ? ApiDimension.DIMENSION_PRODUCT_CATEGORY
        : ApiDimension.DIMENSION_PRODUCT_BRAND,
    [mode],
  );

  return (
    <>
      {(showCategory || showBrand) && (
        <Box sx={{ height: 400, flexShrink: 0, width: "100%" }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_CATEGORIES_TOP}
            staticAttributes={attributes}
            chart={{
              name: `Top Product ${
                mode === "category" ? "Categories" : "Brands"
              }`,
              dimension: [dimension],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_PRODUCT_REVENUE],
                  chart: {
                    [ApiMetric.METRIC_PRODUCT_REVENUE]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
              },
            }}
            customRightTop={
              showCategory && showBrand ? (
                <StyledFlexBox compact sx={{ maxWidth: 300 }}>
                  <Box display="flex" alignItems="center">
                    <Typography>Categories</Typography>
                    <Switch
                      checked={mode === "brand"}
                      onChange={(_e, checked) =>
                        setMode(checked ? "brand" : "category")
                      }
                    />
                    <Typography>Brands</Typography>
                  </Box>
                </StyledFlexBox>
              ) : undefined
            }
          />
        </Box>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%" }}
      >
        {showCategory && (
          <Box sx={{ height: table5Rows, flex: 1, minWidth: 0 }}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_DASHBOARD_CATEGORIES_TABLE}
              staticAttributes={attributes}
              ignoreAttrModel
              chart={{
                name: "Product Category Insights",
                dimension: [ApiDimension.DIMENSION_PRODUCT_CATEGORY],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: insightFields,
                  },
                ],
                displayOptions: {
                  noFilters: true,
                  noAttributionToggle: true,
                  rowsPerPage: 0,
                  showTotals: true,
                },
              }}
            />
          </Box>
        )}

        {showBrand && (
          <Box sx={{ height: table5Rows, flex: 1, minWidth: 0 }}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_DASHBOARD_BRANDS_TABLE}
              staticAttributes={attributes}
              ignoreAttrModel
              chart={{
                name: "Product Brand Stats",
                dimension: [ApiDimension.DIMENSION_PRODUCT_BRAND],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: insightFields,
                  },
                ],
                displayOptions: {
                  noFilters: true,
                  noAttributionToggle: true,
                  rowsPerPage: 0,
                  showTotals: true,
                },
              }}
            />
          </Box>
        )}
      </Stack>

      {!showCategory && !showBrand && (
        <Typography color="text.secondary" textAlign="center" py={4}>
          No product categories or brands are set up for this project yet.
        </Typography>
      )}
    </>
  );
}

export default CategoriesBrandsTab;
